import {skipToken} from '@reduxjs/toolkit/query';
import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Right, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetAvailableLanguagesListQuery} from '@dms/api/base';
import {
  useGetDescriptionGeneratorConfigQuery,
  useUpdateDescriptionGeneratorConfigMutation,
  type Language,
} from '@dms/api/settings';
import {i18n} from '@dms/i18n';
import {settingsRoutes, getWorkspaceFromUri, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

type DescriptionGeneratorForm = {
  enabled: boolean;
  language: Language;
  includeModel: boolean;
  includeEngine: boolean;
  includeFeatures: boolean;
  includeCondition: boolean;
  includeBranch: boolean;
  length: number;
  mentionText: string | null;
  additionalInstruction: string | null;
};

export function DescriptionGeneratorControl() {
  const [updateDescriptionGeneratorConfig, {isLoading: isUpdating}] =
    useUpdateDescriptionGeneratorConfigMutation();
  const {
    data: descriptionGeneratorConfig,
    isLoading,
    isError,
  } = useGetDescriptionGeneratorConfigQuery({});
  const navigate = useNavigate();

  const {workspace} = getWorkspaceFromUri();
  const {data: languageData} = useGetAvailableLanguagesListQuery(
    isNotNil(workspace) ? {workspace} : skipToken
  );

  const languageOptions = Object.entries(languageData?.languages ?? {}).map(([value, label]) => ({
    label,
    value,
  }));

  const onSubmit: FormSubmitHandler<DescriptionGeneratorForm> = (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) =>
    updateDescriptionGeneratorConfig({
      body: data,
    })
      .unwrap()
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );

  return (
    <Card
      title={i18n.t('entity.advertisingPlatforms.labels.enableDescriptionGenerator')}
      isExpandable={false}
      isExpanded={!!descriptionGeneratorConfig?.enabled}
      hasSeparator={false}
      variant="inlineGrey"
      control={{
        type: 'switch',
        value: !!descriptionGeneratorConfig?.enabled,
        isLoading,
        onChange: (isEnabled) => {
          descriptionGeneratorConfig &&
            updateDescriptionGeneratorConfig({
              body: {
                ...descriptionGeneratorConfig,
                enabled: isEnabled,
              },
            })
              .unwrap()
              .catch(handleApiError);
        },
      }}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'action/settings',
          onClick: () => navigate(settingsRoutes.advertisingQuickPresets),
          title: i18n.t('entity.advertisingPlatforms.labels.configureQuickPresets'),
        },
      ]}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Form<DescriptionGeneratorForm>
          values={descriptionGeneratorConfig}
          onSubmit={onSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control) => (
            <VStack spacing={4}>
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.advertisingPlatforms.labels.descriptionGeneratorDescription')}
              </Text>
              <Grid columns={2}>
                <VStack spacing={4}>
                  <FormField
                    name="includeModel"
                    control={control}
                    type="checkbox"
                    label={i18n.t('entity.advertisingPlatforms.labels.includeModel')}
                    helperText={i18n.t(
                      'entity.advertisingPlatforms.labels.includeModelDescription'
                    )}
                  />
                  <FormField
                    name="includeEngine"
                    control={control}
                    type="checkbox"
                    label={i18n.t('entity.advertisingPlatforms.labels.includeEngine')}
                    helperText={i18n.t(
                      'entity.advertisingPlatforms.labels.includeEngineDescription'
                    )}
                  />
                  <FormField
                    name="includeFeatures"
                    control={control}
                    type="checkbox"
                    label={i18n.t('entity.advertisingPlatforms.labels.includeFeatures')}
                    helperText={i18n.t(
                      'entity.advertisingPlatforms.labels.includeFeaturesDescription'
                    )}
                  />
                  <FormField
                    name="includeCondition"
                    control={control}
                    type="checkbox"
                    label={i18n.t('entity.advertisingPlatforms.labels.includeCondition')}
                    helperText={i18n.t(
                      'entity.advertisingPlatforms.labels.includeConditionDescription'
                    )}
                  />
                  <FormField
                    name="includeBranch"
                    control={control}
                    type="checkbox"
                    label={i18n.t('entity.advertisingPlatforms.labels.includeBranch')}
                    helperText={i18n.t(
                      'entity.advertisingPlatforms.labels.includeBranchDescription'
                    )}
                  />
                </VStack>
                <VStack spacing={4}>
                  <FormField
                    name="length"
                    control={control}
                    type="number"
                    decimalPlaces={0}
                    label={i18n.t('entity.advertisingPlatforms.labels.descriptionLength')}
                    tooltip={i18n.t('entity.advertisingPlatforms.labels.descriptionLengthTooltip')}
                    isRequired
                  />
                  <FormField
                    name="mentionText"
                    control={control}
                    type="textarea"
                    isResizable
                    label={i18n.t('entity.advertisingPlatforms.labels.mentionText')}
                    tooltip={i18n.t('entity.advertisingPlatforms.labels.mentionTextTooltip')}
                  />
                  <FormField
                    name="additionalInstruction"
                    control={control}
                    type="textarea"
                    isResizable
                    label={i18n.t('entity.advertisingPlatforms.labels.additionalInstruction')}
                    tooltip={i18n.t(
                      'entity.advertisingPlatforms.labels.additionalInstructionTooltip'
                    )}
                  />
                  <FormField
                    name="language"
                    control={control}
                    type="choice"
                    isRequired
                    isNotClearable
                    label={i18n.t('entity.advertisingPlatforms.labels.language')}
                    options={languageOptions}
                    data-testid={testIds.settings.advertisingPlatforms('language')}
                  />
                </VStack>
              </Grid>
              <Right>
                <FormButton
                  control={control}
                  type="submit"
                  isLoading={isUpdating}
                  title={i18n.t('general.actions.save')}
                />
              </Right>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </Card>
  );
}
